import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useUnmount } from "react-use";
import classNames from "classnames";
import PropTypes from "prop-types";
import { useSecurity } from "~/components/Hooks";
import { useNetworkStateChange } from "~/components/Hooks/useNetworkStateChange";
import {
  Divider,
  ObjectPropertiesWrapper,
  ObjectWrapper,
  DescriptionWrapper,
  Attachments,
  Checkboxes,
  Comments,
  ObjectSubscribersPanel,
} from "~/components/ModalObjects";
import { editDiscussion, fetchDiscussion } from "~/store/actions/actions.discussions";
import { getDiscussionById } from "~/store/selectors/selector.discussions";
import { getProjectMembersById } from "~/store/selectors/selector.projects";
import { isLoggedUserClientSelector } from "~/store/selectors/selector.users";
import FormContainer from "./DiscussionFormContainer";
import { Header } from "./Header";
import ObjectHistory from "../ObjectHistory/ObjectHistory";
import "../ModalObjects/modal-object.less";
import RemindersContainer from "../Reminders/RemindersContainer";

export const Discussion = ({ id, projectId, showNavigation, edit, setEdit, onCloseForm, shouldOverflow }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const discussion = useSelector((state) => getDiscussionById(state, id));
  const isUserClientSelector = useSelector(isLoggedUserClientSelector);
  const projectMembersIds = useSelector((state) => getProjectMembersById(state, projectId));
  const can = useSecurity();
  const canEdit = can(["edit"], discussion);

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries(["discussion-navigation", projectId, id]);
  }, [id, projectId, queryClient]);

  const onChange = useCallback(
    (attributes) => {
      return dispatch(editDiscussion(projectId, id, attributes)).then(() => {
        typeof attributes.is_pinned !== "undefined" && invalidateQuery();
      });
    },
    [dispatch, id, invalidateQuery, projectId]
  );
  const getDiscussion = useCallback(async () => {
    await dispatch(fetchDiscussion(projectId, id)).then(({ error }) => {
      if (error) {
        throw error;
      }
    });
  }, [dispatch, id, projectId]);

  useUnmount(() => {
    if (discussion && !discussion.is_trashed) {
      dispatch(fetchDiscussion(projectId, id));
    }
  });

  useNetworkStateChange(getDiscussion);

  const handleOnClose = useCallback(() => {
    typeof setEdit === "function" && setEdit(false);
    typeof onCloseForm === "function" && onCloseForm();
  }, [onCloseForm, setEdit]);

  return (
    <>
      {!edit ? (
        <ObjectWrapper>
          <div
            className="tw-px-6 tw-pt-6"
            data-testid="discussion-sheet"
          >
            {discussion ? (
              <Header
                id={id}
                name={discussion?.name}
                hiddenFromClients={discussion?.is_hidden_from_clients}
                createdOn={discussion?.created_on}
                createdById={discussion?.created_by_id}
                createdByName={discussion?.created_by_name}
                createdByEmail={discussion?.created_by_email}
                pinned={discussion?.is_pinned}
                projectId={discussion?.project_id}
                className="tw-mb-5"
                onEdit={canEdit ? () => setEdit(true) : null}
                showNavigation={showNavigation}
              />
            ) : null}
            <DescriptionWrapper
              parentId={id}
              parentType="Discussion"
              body={discussion?.body_formatted}
              onDoubleClick={canEdit ? () => setEdit(true) : null}
              className={classNames("tw-mb-4 tw-border-b tw-border-solid tw-border-0 tw-border-border-primary", {
                "tw-pb-4 ": discussion?.body_formatted,
              })}
            />
            <Attachments
              id={id}
              type="Discussion"
              className="tw-mb-4 tw-border-b tw-border-solid tw-border-0 tw-border-border-primary"
            />
          </div>
          <ObjectPropertiesWrapper>
            <ObjectSubscribersPanel
              parentId={id}
              hiddenFromClients={discussion?.is_hidden_from_clients}
              parentType="Discussion"
              canEdit={canEdit}
              members={projectMembersIds}
            />
            {!isUserClientSelector ? (
              <>
                <Divider className="tw-my-4" />
                <Checkboxes
                  hiddenFromClients={discussion?.is_hidden_from_clients}
                  pinned={discussion?.is_pinned}
                  onChange={onChange}
                  className="tw-ml-1"
                  type="discussion"
                />
                <Divider className="tw-my-4" />
                <RemindersContainer
                  parent={discussion}
                  members={projectMembersIds}
                />
              </>
            ) : null}
          </ObjectPropertiesWrapper>
          <div className="tw-px-6">
            <Comments
              hiddenFromClients={discussion?.is_hidden_from_clients}
              parentId={id}
              parentType="Discussion"
              onAddComment={invalidateQuery}
              commentsCount={discussion?.comments_count}
              projectId={projectId}
            />
            <ObjectHistory
              objectId={id}
              objectType="Discussion"
            />
          </div>
        </ObjectWrapper>
      ) : (
        <FormContainer
          projectId={projectId}
          discussionId={id}
          onClose={handleOnClose}
          submitButtonLabel={t("Save")}
          className="tw-p-8"
          shouldOverflow={shouldOverflow}
        />
      )}
    </>
  );
};

Discussion.propTypes = {
  id: PropTypes.number.isRequired,
  projectId: PropTypes.number.isRequired,
  showNavigation: PropTypes.bool,
  edit: PropTypes.bool,
  setEdit: PropTypes.func,
  onCloseForm: PropTypes.func,
  shouldOverflow: PropTypes.bool,
};

Discussion.defaultProps = {
  showNavigation: false,
};
